// frontend/src/features/bapb/modals/CompareHoverContext.jsx
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { INCOME_APPROACHES, resolveIncomeCompareValue } from "./incomeApproachCatalog";

const CompareHoverContext = createContext(null);

export function CompareHoverProvider({ packet, latestByType, draft, children }) {
  const [hover, setHover] = useState(null);
  // hover = { kind:"income", key, label, x, y }

  const incomeApproachRows = useMemo(() => {
    return INCOME_APPROACHES.map((a) => {
      const exists = !!latestByType?.[a.type];
      const sec = draft?.[a.type] || {};
      const inner = sec?.[a.dataKey] || {};
      return { ...a, exists, inner };
    });
  }, [latestByType, draft]);

  const api = useMemo(() => {
    return {
      showIncome: (payload) => setHover(payload),
      hide: () => setHover(null),
      updatePos: (x, y) => setHover((p) => (p ? { ...p, x, y } : p)),
      incomeApproachRows,
    };
  }, [incomeApproachRows]);

  return (
    <CompareHoverContext.Provider value={api}>
      {children}
    </CompareHoverContext.Provider>
  );
}

export function useCompareHover() {
  const ctx = useContext(CompareHoverContext);
  if (!ctx) return null;

  return {
    enterIncome: (e, { key, label }) => {
      const x = e?.clientX ?? 0;
      const y = e?.clientY ?? 0;
      ctx.showIncome({ kind: "income", key, label, x, y });
    },
    move: (e) => {
      if (!e) return;
      ctx.updatePos(e.clientX ?? 0, e.clientY ?? 0);
    },
    leave: () => ctx.hide(),
  };
}

export function CompareHoverPopover() {
  const ctx = useContext(CompareHoverContext);
  if (!ctx || !ctx.incomeApproachRows) return null;

  const h = ctx?.hover;
  if (!h) return null;

  // Currently we only implement Income hover keys (per your request).
  if (h.kind !== "income") return null;

  const style = {
    left: Math.min((h.x || 0) + 14, window.innerWidth - 360),
    top: Math.min((h.y || 0) + 14, window.innerHeight - 240),
  };

  return (
    <div
      className="fixed z-[80] w-[340px] rounded-xl border border-slate-200 bg-white shadow-xl"
      style={style}
    >
      <div className="px-3 py-2 border-b">
        <div className="text-sm font-semibold text-slate-800">{h.label || "Compare"}</div>
        <div className="text-[11px] text-slate-500">Income Approach Comparison</div>
      </div>

      <div className="p-2 space-y-1 max-h-[220px] overflow-auto">
        {ctx.incomeApproachRows.map((a) => {
          const v = resolveIncomeCompareValue(a.inner, h.key);
          return (
            <div
              key={a.type}
              className="flex items-center gap-2 rounded-lg border border-slate-100 px-2 py-1"
            >
              <span
                className={[
                  "inline-block h-2.5 w-2.5 rounded-full",
                  a.exists ? "bg-emerald-500" : "bg-slate-300",
                ].join(" ")}
                title={a.exists ? "Saved" : "Not saved yet"}
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-700 truncate">{a.label}</div>
              </div>
              <div className="text-xs font-medium text-slate-900 text-right">
                {String(v || "—")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}